import React, { FC, useCallback, useEffect, useMemo, useState } from "react"

import { getUserId } from "services/localStorage.service"
import { getEntityService } from "services/entity.service"

import useFetchDataById from "hooks/useFetchDataById"

import { BASE_URL } from "utils/axiosClient"
import { IContextProps } from "utils/IContextProps"

import ENTITY from "constants/entities"

import { IUser } from "models/IUser"
import { ICurrentUserContext } from "./IContext"

const defaultState = {
    currentUser: null,
    setCurrentUser: () => {},
    userId: -1,
    userCompanyId: null,
    isLoading: false,
    reloadCurrentUser: () => {},
    isFeatureEnabled: () => true,
    featuresLoaded: false,
}

export const CurrentUserContext =
    React.createContext<ICurrentUserContext>(defaultState)

const CurrentUserContextProvider: FC<IContextProps> = ({ children }) => {
    const [currentUser, setCurrentUser] = useState<IUser | null>(null)
    const [userCompanyId, setUserCompanyId] = useState<number | null>(null)
    const [features, setFeatures] = useState<Record<string, boolean>>({})
    // False until the first feature map response, so gated menu items and
    // routes are not shown before we know whether they are enabled.
    const [featuresLoaded, setFeaturesLoaded] = useState(false)
    const userId = getUserId()
    const [reload, setReload] = useState(false)

    const { data, isLoading } = useFetchDataById(ENTITY.USER, userId, {
        include: "role,avatar,company,locations",
    }, reload)

    useEffect(() => {
        setCurrentUser(data)
        if(data){
            setUserCompanyId(data?.company?.id)
        }
        // data?.company && setUserCompanyId(data.company.id)
    }, [data])

    // Logout/login does not reload the app, so drop the previous user's map
    // until the new user's one arrives.
    useEffect(() => {
        setFeatures({})
        setFeaturesLoaded(false)
    }, [userId])

    // Load the effective feature map for the logged-in user's company. It is
    // refetched together with the user on (re)initialization so already logged
    // in users pick up new values after a refresh. Superadmin / company-less
    // users get an empty map => everything enabled.
    useEffect(() => {
        if (!data) return
        const loadFeatures = async () => {
            try {
                const response = await getEntityService(
                    ENTITY.COMPANY_FEATURE_FLAGS,
                    "",
                    false,
                    BASE_URL
                )
                setFeatures(response?.features ?? {})
            } catch (error) {
                setFeatures({})
            } finally {
                setFeaturesLoaded(true)
            }
        }
        loadFeatures()
    }, [data])

    const reloadCurrentUser = useCallback(() => {
        setReload((prev) => !prev)
    }, [])

    // Default ON: a key is disabled only when explicitly false in the map.
    const isFeatureEnabled = useCallback(
        (key: string) => features[key] !== false,
        [features]
    )

    const value = useMemo(
        () => ({
            currentUser,
            isLoading,
            setCurrentUser,
            userId,
            userCompanyId,
            reloadCurrentUser,
            isFeatureEnabled,
            featuresLoaded,
        }),
        [
            currentUser,
            isLoading,
            userId,
            userCompanyId,
            reloadCurrentUser,
            isFeatureEnabled,
            featuresLoaded,
        ]
    )

    return (
        <CurrentUserContext.Provider value={value}>
            {children}
        </CurrentUserContext.Provider>
    )
}

export default CurrentUserContextProvider
